/*
This file is located in the main branch
Last time the file was updated (UTC): [11.12.2024 09:21:26]
Permissions to update outside the main branch: [false]
*/

import {
    DropDown,
    DynamicListAddButton,
    DynamicListWrapperProps,
    requiredRule,
    useErrorState,
    ValidatedDynamicList,
    ValidatedDynamicListFormComponentProps,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { getSelectList } from 'axios/dictionaryApi';
import useApi from 'hooks/useApi';
import { OutletContact, OutletContactFaces } from 'models/api/outlet';
import { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Col, Row, Table } from 'reactstrap';
import GetOptions from 'utils/options';
import { OutletFormType } from './OutletChangeModal';
import TwoColumnWrapper from './TwoColumnWrapper';

type TabContanctFacesProps = {
    isOpen?: boolean;
    formHasRole: boolean;
};

const TabContanctFaces = ({ isOpen = false, formHasRole }: TabContanctFacesProps) => {
    const { handleRequestError } = useErrorState();
    const { i18n } = useTranslation();
    const api = useApi();

    const [hasOpen, setHasOpen] = useState(isOpen);
    useEffect(() => {
        setHasOpen((val) => val || isOpen);
    }, [isOpen]);

    const { isFetching, data: сontactTypes } = useQuery(
        ['ContactTypes', i18n.language],
        () => getSelectList(api, ['ContactTypes'], i18n.language),
        {
            enabled: hasOpen,
            onError: handleRequestError,
        }
    );

    const formComponent = useCallback(
        (props: ValidatedDynamicListFormComponentProps) => (
            <PersoneForm
                {...props}
                formHasRole={formHasRole}
                сontactTypes={сontactTypes ? сontactTypes['ContactTypes'] : []}
                сontactTypesIsFetching={isFetching}
            />
        ),
        [formHasRole, сontactTypes, isFetching]
    );

    const wrapper = useCallback((props: DynamicListWrapperProps) => <Wrapper {...props} />, []);

    return (
        <ValidatedDynamicList<OutletFormType, OutletContactFaces>
            disabled={!formHasRole}
            formComponent={formComponent}
            wrapper={wrapper}
            prepend={false}
            emptyRecord={{
                name: '',
                dsc: '',
                contacts: [
                    {
                        id: null,
                        type: '',
                        value: '',
                    },
                ],
            }}
            name="contactFaces"
        />
    );
};

const ContactsForm = ({
    idxRoot,
    idx,
    formHasRole,
    сontactTypes,
    сontactTypesIsFetching,
}: ValidatedDynamicListFormComponentProps & {
    formHasRole: boolean;
    idxRoot: number;
    сontactTypes: DropDown[];
    сontactTypesIsFetching: boolean;
}) => {
    const { t } = useTranslation();

    return (
        <>
            <td>
                <ValidatedInput<OutletFormType>
                    type="select"
                    name={`contactFaces.${idxRoot}.contacts.${idx}.type`}
                    label={t('BackOfficeContactType')}
                    disabled={!formHasRole}
                    rules={{
                        required: requiredRule,
                    }}
                    options={GetOptions(сontactTypes, true, false)}
                    loading={сontactTypesIsFetching}
                />
            </td>
            <td>
                <ValidatedInput<OutletFormType>
                    name={`contactFaces.${idxRoot}.contacts.${idx}.value`}
                    label={t('BackOfficeContactValue')}
                    disabled={!formHasRole}
                    rules={{
                        required: requiredRule,
                    }}
                />
            </td>
        </>
    );
};

const PersoneForm = ({
    idx,
    formHasRole,
    сontactTypes,
    сontactTypesIsFetching,
}: ValidatedDynamicListFormComponentProps & {
    formHasRole: boolean;
    сontactTypes: DropDown[];
    сontactTypesIsFetching: boolean;
}) => {
    const { t } = useTranslation();

    const formComponent = useCallback(
        (props: ValidatedDynamicListFormComponentProps) => (
            <ContactsForm
                {...props}
                formHasRole={formHasRole}
                idxRoot={idx}
                сontactTypes={сontactTypes}
                сontactTypesIsFetching={сontactTypesIsFetching}
            />
        ),
        [formHasRole, idx, сontactTypes, сontactTypesIsFetching]
    );

    const wrapper = useCallback(
        (props: DynamicListWrapperProps) => (
            <TwoColumnWrapper
                {...props}
                col1Label="BackOfficeContactType"
                col2Label="BackOfficeContactValue"
            />
        ),
        []
    );

    return (
        <td>
            <Row>
                <Col>
                    <ValidatedInput<OutletFormType>
                        name={`contactFaces.${idx}.name`}
                        label={t('BackOfficeName')}
                        disabled={!formHasRole}
                        rules={{
                            required: requiredRule,
                        }}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <ValidatedDynamicList<OutletFormType, OutletContact>
                        disabled={!formHasRole}
                        formComponent={formComponent}
                        wrapper={wrapper}
                        prepend={false}
                        emptyRecord={{
                            id: null,
                            type: '',
                            value: '',
                        }}
                        name={`contactFaces.${idx}.contacts`}
                        compareFunc={(a, b) => a?.type === b?.type}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <ValidatedInput<OutletFormType>
                        type="textarea"
                        name={`contactFaces.${idx}.dsc`}
                        label={t('BackOfficeDescription')}
                        disabled={!formHasRole}
                    />
                </Col>
            </Row>
        </td>
    );
};

const Wrapper = ({ children, ...props }: DynamicListWrapperProps) => {
    return (
        <Table bordered>
            <thead>
                <tr className="align-middle">
                    <th className="w-45">
                        <Trans i18nKey="BackOfficeNewContact" />
                    </th>
                    <th className="w-10 text-center">
                        <DynamicListAddButton {...props} />
                    </th>
                </tr>
            </thead>
            <tbody>{children}</tbody>
        </Table>
    );
};

export default TabContanctFaces;
